import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import axios from 'axios';

export default function BankDetailsModal({ open, onClose, artistId, artistName, authToken }) {
    const [bankDetails, setBankDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && artistId) {
            fetchBankDetails();
        }
    }, [open, artistId]);

    const fetchBankDetails = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API}/banking/get-decrypted-bank-details/${artistId}`,
                { headers: { 'AuthToken': authToken } }
            );

            setBankDetails(response.data);
        } catch (error) {
            console.error('Failed to fetch bank details:', error);
            setError('Failed to load bank details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setBankDetails(null);
        setError(null);
        onClose();
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatSortCode = (sortCode) => {
        if (!sortCode) return '';
        // Remove any existing dashes and format as xx-xx-xx
        const cleanSortCode = sortCode.replace(/-/g, '');
        if (cleanSortCode.length === 6) {
            return `${cleanSortCode.slice(0, 2)}-${cleanSortCode.slice(2, 4)}-${cleanSortCode.slice(4, 6)}`;
        }
        return sortCode; // Return as-is if not 6 digits
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="bank-details-modal-title"
            aria-describedby="bank-details-modal-description"
        >
            <Box sx={modalStyle}>
                <Typography id="bank-details-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                    Bank Details - {artistName}
                </Typography>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {bankDetails && !loading && (
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Account Holder</TableCell>
                                    <TableCell>{bankDetails.accountHolderName}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Account Type</TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>{bankDetails.accountType}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Sort Code</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '1.1em' }}>
                                        {formatSortCode(bankDetails.sortCode)}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Account Number</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '1.1em' }}>
                                        {bankDetails.accountNumber}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Created</TableCell>
                                    <TableCell>{formatDate(bankDetails.createdAt)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        sx={{ backgroundColor: '#3496d1' }}
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    maxWidth: '90%',
    maxHeight: '80%',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    overflow: 'auto',
}; 